import React, { useRef, useState } from 'react';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { ExtendedTableLowCode } from '@/components/shared/table/ExtendedTableLowCode';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import { Button, CustomCellProperties, Dialog, ModalRef } from 'alurkerja-ui';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { TestIssueService } from '@/api/testIssue';
import { IFilterSpec } from '@/components/shared/filter/TableFilterPanel';
import { useBackNavigate } from '@/utils/hooks/useBackNavigate';
import { HideCustomCreateButton } from '@/components/shared/button/HideCustomButtonCreate';

export const ListArchiveTestIssue: React.FC = () => {
  const { id } = useParams();
  const backNavigate = useBackNavigate();
  const ref = useRef<ModalRef>(null);

  const { data: optionData } = TestIssueService.useGetDropDown(id);

  const spec = useCustomSpec('/api/crud/test-issue', {
    columns: [
      { key: 'code_issue', label: 'Code' },
      { key: 'title', label: 'Title' },
      { key: 'created_by', label: 'Created by' },
      { key: 'assignee_issue', label: 'Assigned to' },
      { key: 'status', label: 'Status' },
      { key: 'priority', label: 'Priority' },
      { key: 'created_at', label: 'Created Date' },
    ],
  });

  const optionUser = optionData?.assignee_issue.map(item => ({
    label: item.label,
    value: item.label,
  }));

  const filterSpec: IFilterSpec[] = [
    { name: 'code_issue', label: 'Issue Code', type: 'text' },
    { name: 'title', label: 'Title', type: 'text' },
    {
      name: 'created_by',
      label: 'Created by',
      type: 'option',
      options: optionUser,
    },
    {
      name: 'assignee_to',
      label: 'Assigned To',
      type: 'option',
      options: optionUser,
    },
    {
      name: 'status',
      label: 'Status',
      type: 'option',
      options: optionData?.status,
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'option',
      options: optionData?.priority,
    },
    { name: 'created_at', label: 'Created Date', type: 'date' },
  ];

  const customCell = ({
    name,
    rowValue,
    value,
    defaultCell,
  }: CustomCellProperties) => {
    if (name === 'assignee_issue') {
      return <>{rowValue.assignee ? rowValue.assignee.full_name : '-'}</>;
    } else if (name === 'created_by') {
      return <>{value ? value.full_name : '-'}</>;
    } else if (name === 'created_at') {
      return <>{moment(value).format('DD MMMM YYYY')}</>;
    }

    return defaultCell;
  };

  const [selectedRow, setSelectedRow] = useState<string[]>([]);
  const { mutate: unArchiveMutate, isPending: isUnarchiveLoading } =
    TestIssueService.useUnarchive();

  const unarchiveAction = (idList: string[]) => {
    idList.forEach(id => {
      unArchiveMutate(id, {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Issue has been Unarchived',
            callback: () => {
              ref.current?.closeModal();
              backNavigate();
            },
          });
        },
        onError: () => {
          Dialog.error({
            title: 'Error',
            description: 'Failed to Unarchive Issue',
          });
        },
      });
    });
  };

  return (
    <div className="tab-group">
      <div className="justify-between tab-list">
        <OutlineButton onClick={backNavigate}>Back</OutlineButton>

        <Button
          onClick={() => unarchiveAction(selectedRow)}
          loading={isUnarchiveLoading}
          disabled={selectedRow.length === 0}
        >
          Revert Back
        </Button>
      </div>

      <div className="mx-4">
        <ExtendedTableLowCode
          spec={spec}
          defaultFilter={{ project_id: id, is_archive: true }}
          hideActionColumn
          customButtonCreate={HideCustomCreateButton}
          customCell={customCell}
          showFilter={true}
          filterSpec={filterSpec}
          showCheckbox
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
        />
      </div>
    </div>
  );
};
